import React, { useState } from "react";
import { toast } from "react-toastify";
import NumberField from "../../../Components/NumberField";
import { currencyFormatter } from "../../../Helpers/Utils";
import Handler from "../../../Helpers/Handler";
import { invalidClass, numberError, RULES } from "../../../Helpers/bookingRules";
import { FaCloudUploadAlt } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import tripUrls from "./tripUrls";

// The booking's damage record (one per booking), open from Ended until the fine charges are submitted.
// Booking Flow v2: staff enter the repair cost; when the damage is chargeable the customer pays what is
// above the trip protection's cover value (cost 32,000 with 12,000 cover -> 20,000). Trip protection
// covers damage only, never excess or other fines. Saving again updates the same record.
//
// PATCH <damage url>, multipart: damageImages[], damageStatus, service, deductible (Yes = charge the
// customer), repairCost. Service = Yes takes the car out of service until it is repaired.

const MAX_IMAGES = RULES.TRIP_IMAGES_MAX;

const DAMAGE_STATUS_OPTIONS = [
    { value: "minor", label: "Minor" },
    { value: "major", label: "Major" },
    { value: "nonRecoverable", label: "Non-recoverable" },
];

const DamageModal = ({ bookingId, existing, coverAmount, canWaive = true, onClose, onLogged }) => {
    const [imageTemp, setImageTemp] = useState([]); // [{imgId, file, url}]
    const [damageStatus, setDamageStatus] = useState(existing?.damageStatus || "");
    const [service, setService] = useState(existing?.service === "Yes" ? "Yes" : "No");
    const [deductible, setDeductible] = useState(existing ? (existing.deductible === false ? "No" : "Yes") : canWaive ? "" : "Yes");
    const [repairCost, setRepairCost] = useState(existing?.repairCost != null ? String(existing.repairCost) : "");
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const savedImages = existing?.damageImages || [];
    const room = MAX_IMAGES - savedImages.length - imageTemp.length;

    const cover = Number(coverAmount || 0);
    const cost = Number(repairCost || 0);
    const fine = deductible === "Yes" && cost > 0 ? Math.max(0, Math.round((cost - cover) * 100) / 100) : 0;

    const imageChange = (e) => {
        const files = Object.values(e.target.files || {});
        if (!files.length) return;
        let left = room;
        if (files.length > left) toast.error(`Only ${MAX_IMAGES} photos are allowed; ${Math.max(left, 0)} more can be added`);
        files.forEach((file) => {
            if (left <= 0) return;
            if (!/^image\//.test(file.type)) { toast.error(`${file.name} is not an image`); return; }
            if (file.size <= RULES.IMAGE_MAX_BYTES) {
                left -= 1;
                setImageTemp((prev) => [...prev, { imgId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, file, url: URL.createObjectURL(file) }]);
            } else {
                toast.error("Image must be below 10 MB");
            }
        });
        e.target.value = "";
    };

    const removeImage = (imgId) => setImageTemp((prev) => prev.filter((v) => v.imgId !== imgId));

    const submitDamage = async () => {
        const next = {
            damageStatus: damageStatus ? null : "Select a damage status",
            deductible: deductible ? null : "Choose whether the customer is charged",
            repairCost: deductible === "Yes"
                ? numberError(repairCost, { label: "Repair cost", required: true, gt: 0, max: RULES.MONEY_MAX, maxDecimals: 2 })
                : numberError(repairCost, { label: "Repair cost", min: 0, max: RULES.MONEY_MAX, maxDecimals: 2 }),
        };
        setErrors(next);
        const problem = next.damageStatus || next.deductible || next.repairCost;
        if (problem) { toast.error(problem); return; }

        const formData = new FormData();
        imageTemp.forEach((v) => formData.append("damageImages", v.file));
        formData.append("damageStatus", damageStatus);
        formData.append("service", service);
        formData.append("deductible", deductible);
        if (repairCost !== "") formData.append("repairCost", String(Number(repairCost)));

        setSubmitting(true);
        const response = await Handler({ method: "patch", url: tripUrls.damage(bookingId), data: formData, formdata: true });
        setSubmitting(false);
        if (response?.success) {
            toast.success(existing ? "Damage updated" : "Damage recorded");
            onLogged?.();
            onClose?.();
        } else {
            toast.error(response?.data?.message || "Could not record this damage");
        }
    };

    const yesNo = (value, setValue, { danger = "Yes", disabledNo = false, onPick } = {}) => (
        <div className="btn-group" role="group">
            {["No", "Yes"].map((option) => (
                <button
                    key={option}
                    type="button"
                    className={`btn btn-sm ${value === option ? (option === danger ? "btn-danger" : "btn-secondary") : option === danger ? "btn-outline-danger" : "btn-outline-secondary"}`}
                    onClick={() => { setValue(option); onPick?.(); }}
                    disabled={submitting || (option === "No" && disabledNo)}
                    aria-pressed={value === option}
                >
                    {option}
                </button>
            ))}
        </div>
    );

    return (
        <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{existing ? "Edit Damage" : "Add Damage"}</h5>
                            <button type="button" className="btn-close" onClick={onClose} disabled={submitting}></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label className="form-label small">Damage status</label>
                                <select className={`form-select${invalidClass(errors.damageStatus)}`} value={damageStatus} onChange={(e) => { setDamageStatus(e.target.value); setErrors((p) => ({ ...p, damageStatus: null })); }} disabled={submitting} aria-invalid={!!errors.damageStatus}>
                                    <option value="">Select status</option>
                                    {DAMAGE_STATUS_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                                {errors.damageStatus && <div className="invalid-feedback">{errors.damageStatus}</div>}
                            </div>

                            <div className="mb-3">
                                <label className="form-label small d-block">Service required?</label>
                                {yesNo(service, setService)}
                                {service === "Yes" && <div className="form-text text-danger">Marking this Yes takes the car out of service until it's repaired.</div>}
                            </div>

                            <div className="mb-3">
                                <label className="form-label small d-block">Charge the customer for this damage?</label>
                                {yesNo(deductible, setDeductible, { disabledNo: !canWaive, onPick: () => setErrors((p) => ({ ...p, deductible: null, repairCost: null })) })}
                                {errors.deductible && <div className="text-danger small mt-1">{errors.deductible}</div>}
                                {!canWaive && <div className="form-text">Not charging the customer needs extra permission.</div>}
                            </div>

                            <div className="mb-3">
                                <label className="form-label small">Repair cost (₹){deductible === "Yes" ? "" : " (optional)"}</label>
                                <NumberField
                                    decimals={2}
                                    min={0}
                                    max={RULES.MONEY_MAX}
                                    className={`form-control${invalidClass(errors.repairCost)}`}
                                    value={repairCost}
                                    onChange={(v) => { setRepairCost(v); setErrors((p) => ({ ...p, repairCost: null })); }}
                                    disabled={submitting}
                                    aria-invalid={!!errors.repairCost}
                                />
                                {errors.repairCost && <div className="invalid-feedback">{errors.repairCost}</div>}
                                <div className="form-text">
                                    Trip protection covers up to {currencyFormatter(cover)} of damage on this booking.
                                    {deductible === "Yes" && cost > 0 && <> The customer pays <strong>{currencyFormatter(fine)}</strong> ({currencyFormatter(cost)} repair - {currencyFormatter(cover)} cover).</>}
                                    {deductible === "No" && " The customer is not charged for this damage."}
                                </div>
                            </div>

                            <hr />
                            <div className="mb-2 small text-muted">
                                Photos of the damage (optional, up to {MAX_IMAGES}{savedImages.length ? `; ${savedImages.length} already saved` : ""}).
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                                {savedImages.map((url) => <img key={url} src={url} alt="" width={84} height={84} className="rounded-3 border" style={{ objectFit: "cover" }} />)}
                                {imageTemp.map((v) => (
                                    <div key={v.imgId} className="position-relative">
                                        <img src={v.url} alt="" width={84} height={84} className="rounded-3 border" style={{ objectFit: "cover" }} />
                                        <button type="button" className="btn btn-sm btn-danger p-0 rounded-circle position-absolute" style={{ top: -6, right: -6, width: 20, height: 20, lineHeight: "20px" }} onClick={() => removeImage(v.imgId)} title="Remove">
                                            <ImCross size={8} />
                                        </button>
                                    </div>
                                ))}
                                {room > 0 && (
                                    <label className="d-flex align-items-center justify-content-center rounded-3 border text-muted" style={{ width: 84, height: 84, cursor: submitting ? "not-allowed" : "pointer" }}>
                                        <FaCloudUploadAlt size={20} />
                                        <input type="file" multiple accept="image/png,image/jpeg,image/webp" className="d-none" onChange={imageChange} disabled={submitting} />
                                    </label>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
                            <button type="button" className="btn btn-primary" onClick={submitDamage} disabled={submitting}>
                                {submitting ? "Saving..." : "Save Damage"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};

export default DamageModal;
